import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const AgentConstellation = () => {
  const { data: agents, loading, error, retry } = useAsyncData('/api/agents');
  const { isStale, lastUpdated } = useStaleWarning(agents);
  useAutoRefresh(retry, { interval: 30 });

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <section className="panel agent-panel" aria-busy="true" aria-label="Agent constellation">
          <h2>Agents</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </section>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <section className="panel agent-panel" aria-live="assertive">
          <h2>Agents</h2>
          <p className="error-msg">Failed to load agents.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </section>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <section className="panel agent-panel" aria-label="Agent constellation" aria-busy="false">
        <div className="panel-header">
          <h2>Agents</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh agents">
              ↻
            </button>
          </div>
        </div>
        <ul className="agent-list" aria-label="Agent states">
          {(agents || []).length === 0 && <li className="empty">No agents reported</li>}
          {(agents || []).map((a) => (
            <li key={a.id} className={`agent-badge ${a.status}`}>
              <span className="agent-name">{a.name}</span>
              <span className="agent-status">{a.status}</span>
            </li>
          ))}
        </ul>
      </section>
    </ErrorBoundary>
  );
};

export default AgentConstellation;
